import { Fragment, useState, type ReactNode } from 'react'
import { toast } from 'sonner'
import {
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Edit2,
  Mail,
  MessageSquare,
  Paperclip,
  RefreshCw,
  Send,
} from 'lucide-react'
import { type ApInvoiceThread } from '@/features/invoices/view-models'
import { ACCOUNTING_SIGNOFF } from '@/features/communications/formatting'
import { type ApInvoiceThreadTemplate } from '@/features/invoices/types'
import { useCreateInvoiceThread } from '@/features/invoices/data/communications'
import { useInvoice, useInvoiceThreadDefaults } from '@/features/invoices/queries'
import {
  useCloseThread,
  useReopenThread,
  useSendMessage,
  useSendReply,
  useUpdateThreadRecipients,
} from '@/features/communications/hooks'
import { LoadingButton } from '@/components/loading-button'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { SanitizedEmailHtml } from '@/components/sanitized-email-html'
import { stripQuotedHtml, stripQuotedText } from '@/lib/email-body'
import { formatStatus, parseUTCDate } from '@/lib/utils'
import { parseEmailList } from './formatting'
import { ComposeInput, ComposeTextarea } from '@/components/compose-field'
import {
  InlineFieldGroup,
  type InlineGroupField,
} from '@/components/inline-field-group'
import { getApiErrorMessage } from '@/lib/api-error'

function threadStatusBadge(status: string) {
  const variants: Record<string, 'default' | 'secondary' | 'outline' | 'destructive'> = {
    draft: 'outline',
    sending: 'secondary',
    sent: 'secondary',
    awaiting_reply: 'secondary',
    replied: 'default',
    needs_attention: 'destructive',
    auto_responded: 'default',
    resolved: 'outline',
    archived: 'outline',
    error: 'destructive',
  }
  return <Badge variant={variants[status] || 'secondary'}>{formatStatus(status)}</Badge>
}

function InvoiceMessageBubble({
  message,
}: {
  message: NonNullable<ApInvoiceThread['messages']>[number]
}) {
  const isOutbound = message.direction === 'outbound'
  const cleanHtml = message.body_html ? stripQuotedHtml(message.body_html) : null
  const cleanText = message.body_text ? stripQuotedText(message.body_text) : ''
  const toEmails = parseEmailList(message.to_emails)
  const ccEmails = parseEmailList(message.cc_emails)
  const sendMessage = useSendMessage()
  return (
    <div
      id={`invoice-message-${message.id}`}
      className={`flex scroll-mt-20 ${isOutbound ? 'justify-end' : 'justify-start'}`}
    >
      <div
        className={`min-w-0 max-w-[92%] space-y-1 overflow-hidden rounded-lg px-3 py-2.5 sm:max-w-[85%] sm:px-4 sm:py-3 md:max-w-[75%] ${isOutbound ? 'bg-blue-50 dark:bg-blue-950' : 'bg-muted'}`}
      >
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
          <span className="font-medium">
            {isOutbound
              ? message.from_name || 'USA-RS'
              : message.from_name || message.from_email || 'Sender'}
          </span>
          <span>
            {parseUTCDate(
              message.sent_at || message.received_at || message.created_at,
            ).toLocaleString()}
          </span>
          <Badge variant="outline" className="h-5 text-[10px]">
            {formatStatus(message.status)}
          </Badge>
        </div>
        {message.subject && (
          <p className="text-xs font-medium text-muted-foreground">{message.subject}</p>
        )}
        {(toEmails.length > 0 || ccEmails.length > 0) && (
          <div className="space-y-0.5 text-[11px] text-muted-foreground">
            {toEmails.length > 0 && (
              <div>
                <span className="font-semibold">To:</span> {toEmails.join(', ')}
              </div>
            )}
            {ccEmails.length > 0 && (
              <div>
                <span className="font-semibold">Cc:</span> {ccEmails.join(', ')}
              </div>
            )}
          </div>
        )}
        {cleanHtml ? (
          <SanitizedEmailHtml html={cleanHtml} />
        ) : (
          <div className="whitespace-pre-wrap text-sm">
            {cleanText || 'No message body recorded.'}
          </div>
        )}
        {message.has_attachments && (
          <div className="flex flex-wrap gap-1 pt-1 text-[11px] text-muted-foreground">
            <Paperclip className="h-3 w-3" />{' '}
            {parseEmailList(message.attachment_filenames).join(', ') || 'Attachments'}
          </div>
        )}
        {message.status === 'draft' && isOutbound && (
          <div className="flex gap-2 pt-2">
            <LoadingButton
              size="sm"
              loading={sendMessage.isPending && sendMessage.variables === message.uuid}
              loadingText="Sending..."
              onClick={() =>
                sendMessage.mutate(message.uuid, {
                  onSuccess: () => toast.success('Draft sent'),
                  onError: (err) => toast.error(getApiErrorMessage(err, 'Send failed')),
                })
              }
            >
              <Send className="mr-1 h-3 w-3" /> Send
            </LoadingButton>
          </div>
        )}
      </div>
    </div>
  )
}

type RecipientKey = 'email' | 'name' | 'cc'

const RECIPIENT_FIELDS: ReadonlyArray<InlineGroupField<RecipientKey>> = [
  {
    key: 'email',
    label: 'To',
    required: true,
    autoComplete: 'email',
    inputMode: 'email',
    className: 'col-span-6 sm:col-span-3',
  },
  { key: 'name', label: 'To display name', className: 'col-span-6 sm:col-span-3' },
  { key: 'cc', label: 'CC', inputMode: 'email' },
]

/** The thread's saved TO/CC recipients, edited in place under its header (USA-1580). */
function ThreadRecipientsEditor({
  thread,
  onClose,
}: {
  thread: ApInvoiceThread
  onClose: () => void
}) {
  const updateRecipients = useUpdateThreadRecipients()
  const saveRecipients = (draft: Record<RecipientKey, string>) => {
    updateRecipients.mutate(
      {
        threadUuid: thread.uuid,
        recipient_email: draft.email.trim(),
        recipient_name: draft.name.trim() || null,
        cc_emails: parseEmailList(draft.cc),
      },
      {
        onSuccess: () => {
          toast.success('Recipients saved')
          onClose()
        },
        onError: (err) =>
          toast.error(getApiErrorMessage(err, 'Recipient update failed')),
      },
    )
  }
  return (
    <InlineFieldGroup
      label="Thread recipients"
      fields={RECIPIENT_FIELDS}
      values={{
        email: thread.recipient_email ?? '',
        name: thread.recipient_name ?? '',
        cc: parseEmailList(thread.cc_emails).join(', '),
      }}
      labelled
      interactiveChildren
      editing
      onEditingChange={(next) => {
        if (!next) onClose()
      }}
      pending={updateRecipients.isPending}
      saveLabel="Save recipients"
      notice="Updates the same stored TO/CC values used by PO communication replies."
      onSave={saveRecipients}
    >
      {null}
    </InlineFieldGroup>
  )
}

export function NewInvoiceThreadDialog({
  invoice,
}: {
  invoice: NonNullable<ReturnType<typeof useInvoice>['data']>
}) {
  const [open, setOpen] = useState(false)
  const [template, setTemplate] = useState<ApInvoiceThreadTemplate>('ap_invoice')
  const invoiceUuid = invoice.uuid ?? ''
  const defaults = useInvoiceThreadDefaults(invoiceUuid, template)
  const createThread = useCreateInvoiceThread(invoiceUuid)
  const [recipientEmail, setRecipientEmail] = useState(invoice.vendor?.contact_email ?? '')
  const [recipientName, setRecipientName] = useState(
    invoice.vendor?.name ?? invoice.vendor_raw ?? '',
  )
  const [ccEmails, setCcEmails] = useState('')
  const [subject, setSubject] = useState(
    `AP invoice ${invoice.invoice_number || invoice.vendor?.name || 'record'}`,
  )
  const [bodyText, setBodyText] = useState('')
  const [attachInvoicePdf, setAttachInvoicePdf] = useState(Boolean(invoice.attachments?.length))
  const applyDefaults = () => {
    setRecipientEmail(defaults.data?.email ?? invoice.vendor?.contact_email ?? '')
    setRecipientName(defaults.data?.name ?? invoice.vendor?.name ?? invoice.vendor_raw ?? '')
    setCcEmails((defaults.data?.cc_emails ?? []).join(', '))
  }
  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen)
    if (nextOpen) applyDefaults()
  }
  const handleSubmit = () => {
    createThread.mutate(
      {
        template,
        subject: subject.trim() || undefined,
        recipient_email: recipientEmail.trim(),
        recipient_name: recipientName.trim() || null,
        cc_emails: parseEmailList(ccEmails),
        body_text: bodyText.trim() || undefined,
        attach_invoice_pdf: attachInvoicePdf,
      },
      {
        onSuccess: () => {
          toast.success('Thread created')
          setOpen(false)
        },
        onError: (err) =>
          toast.error(getApiErrorMessage(err, 'Thread creation failed')),
      },
    )
  }
  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
        <MessageSquare className="mr-1 h-3 w-3" /> New Thread
      </Button>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>New communication thread</DialogTitle>
          <DialogDescription>
            Uses AP invoice thread endpoints with editable default recipients.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs text-muted-foreground">Template</Label>
            <Select
              value={template}
              onValueChange={(value) => setTemplate(value as ApInvoiceThreadTemplate)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ap_invoice">AP invoice</SelectItem>
                <SelectItem value="blank">Blank</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">
              To {defaults.isLoading && <span className="italic">— loading defaults...</span>}
            </Label>
            <ComposeInput
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value)}
              placeholder="vendor@example.com"
            />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">To display name</Label>
            <ComposeInput value={recipientName} onChange={(e) => setRecipientName(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">CC (comma-separated)</Label>
            <ComposeInput value={ccEmails} onChange={(e) => setCcEmails(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Subject</Label>
            <ComposeInput value={subject} onChange={(e) => setSubject(e.target.value)} />
          </div>
          {template === 'blank' && (
            <div>
              <Label className="text-xs text-muted-foreground">Body</Label>
              <ComposeTextarea
                value={bodyText}
                onChange={(e) => setBodyText(e.target.value)}
                rows={6}
                placeholder="Optional draft body..."
              />
            </div>
          )}
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={attachInvoicePdf}
              onChange={(e) => setAttachInvoicePdf(e.target.checked)}
              disabled={!invoice.attachments?.length}
            />{' '}
            Attach invoice PDF{' '}
            {!invoice.attachments?.length && (
              <span className="text-muted-foreground">(not available)</span>
            )}
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <LoadingButton
            loading={createThread.isPending}
            loadingText="Creating..."
            onClick={handleSubmit}
            disabled={!recipientEmail.trim()}
          >
            <MessageSquare className="mr-1 h-3 w-3" /> Create thread
          </LoadingButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function InvoiceThreadCard({
  thread,
  renderMessageActions,
}: {
  thread: ApInvoiceThread
  /** The open action items an email raised, shown under it so they can be handled there (USA-1418). */
  renderMessageActions?: (messageId: number) => ReactNode
}) {
  const messages = thread.messages ?? []
  const [replyText, setReplyText] = useState('')
  const [expanded, setExpanded] = useState(true)
  const [editingRecipients, setEditingRecipients] = useState(false)
  const sendReply = useSendReply(thread.uuid)
  const closeThread = useCloseThread()
  const reopenThread = useReopenThread()
  const isClosed = thread.status === 'resolved' || thread.status === 'archived'

  const submitReply = () => {
    const body = replyText.trim()
    if (!body) return
    sendReply.mutate(
      { body_text: `${body}\n\n${ACCOUNTING_SIGNOFF}` },
      {
        onSuccess: () => {
          toast.success('Reply sent')
          setReplyText('')
        },
        onError: (err) => toast.error(getApiErrorMessage(err, 'Reply failed')),
      },
    )
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <button
            type="button"
            className="flex min-w-0 flex-1 items-start gap-2 text-left"
            onClick={() => setExpanded((current) => !current)}
            aria-expanded={expanded}
          >
            {expanded ? (
              <ChevronDown className="mt-0.5 h-4 w-4 shrink-0" />
            ) : (
              <ChevronRight className="mt-0.5 h-4 w-4 shrink-0" />
            )}
            <span className="min-w-0 space-y-1">
              <CardTitle className="flex items-center gap-2 text-base">
                <Mail className="h-4 w-4" /> {thread.subject || 'Invoice email thread'}
              </CardTitle>
              <span className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
                <span>To {thread.recipient_name || thread.recipient_email}</span>
                {thread.cc_emails && <span>Cc {parseEmailList(thread.cc_emails).join(', ')}</span>}
                <span>
                  Last {parseUTCDate(thread.last_activity_at || thread.updated_at).toLocaleString()}
                </span>
              </span>
            </span>
          </button>
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            {threadStatusBadge(thread.status)}
            <Button
              size="sm"
              variant="outline"
              aria-expanded={editingRecipients}
              onClick={() => setEditingRecipients(true)}
            >
              <Edit2 className="mr-1 h-3 w-3" /> Edit recipients
            </Button>
            {isClosed ? (
              <LoadingButton
                size="sm"
                variant="outline"
                loading={reopenThread.isPending}
                loadingText="Reopening..."
                onClick={() =>
                  reopenThread.mutate(
                    { threadUuid: thread.uuid, status: 'awaiting_reply' },
                    {
                      onSuccess: () => toast.success('Thread reopened'),
                      onError: (err) =>
                        toast.error(getApiErrorMessage(err, 'Failed to reopen thread')),
                    },
                  )
                }
              >
                <RefreshCw className="mr-1 h-3 w-3" /> Reopen
              </LoadingButton>
            ) : (
              <LoadingButton
                size="sm"
                variant="outline"
                loading={closeThread.isPending}
                loadingText="Closing..."
                onClick={() =>
                  closeThread.mutate(thread.uuid, {
                    onSuccess: () => toast.success('Thread closed'),
                    onError: (err) =>
                      toast.error(getApiErrorMessage(err, 'Failed to close thread')),
                  })
                }
              >
                <CheckCircle2 className="mr-1 h-3 w-3" /> Close
              </LoadingButton>
            )}
          </div>
        </div>
        {editingRecipients && (
          <div className="border-t pt-3">
            <ThreadRecipientsEditor thread={thread} onClose={() => setEditingRecipients(false)} />
          </div>
        )}
      </CardHeader>
      {expanded && (
        <CardContent className="space-y-3">
          {messages.length ? (
            messages.map((message) => (
              <Fragment key={message.uuid}>
                <InvoiceMessageBubble message={message} />
                {renderMessageActions?.(message.id)}
              </Fragment>
            ))
          ) : (
            <p className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
              This linked thread has no message bodies in the AP response yet.
            </p>
          )}
          <div className="space-y-2 rounded-lg border bg-muted/20 p-3">
            <Label className="text-xs text-muted-foreground">Reply</Label>
            <ComposeTextarea
              aria-label="Reply to invoice thread"
              value={replyText}
              onChange={(event) => setReplyText(event.target.value)}
              rows={4}
              placeholder="Write a reply using this thread's stored TO/CC recipients..."
            />
            <p className="whitespace-pre-line text-xs text-muted-foreground">{ACCOUNTING_SIGNOFF}</p>
            <LoadingButton
              size="sm"
              loading={sendReply.isPending}
              loadingText="Sending..."
              onClick={submitReply}
              disabled={!replyText.trim()}
            >
              <Send className="mr-1 h-3 w-3" /> Send reply
            </LoadingButton>
          </div>
        </CardContent>
      )}
    </Card>
  )
}
